import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, HeartOutlined, SafetyOutlined, BulbOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import squishyLionImage from '../../../assets/images/Squish Lion.png';

const { Title, Text, Paragraph } = Typography;

const CalmiSquishyLion = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('lumi-squishy-lion', 'Lumi Squishy Lion', 499);
  }, []);

  const product = {
    id: 'lumi-squishy-lion',
    name: 'Lumi Squishy Lion',
    price: 499,
    image: squishyLionImage,
    collection: 'Squishy Collection',
    subtitle: 'Courageous round friend',
    description: 'A courageous lion in adorable, round squishy form with golden plush and a fluffy mane. This brave little companion combines confidence-inspiring nature with ultimate squeezability.',
    fullDescription: 'Meet the Lumi Squishy Lion - courage and confidence in the most huggable form possible. This adorable, round lion combines the bravery-inspiring nature of a lion with the ultimate squeezable comfort. Featuring golden plush with a fluffy mane and an irresistibly round shape, this companion is perfect for children who need courage but also love tactile comfort. The warm golden chest light represents inner strength, while the squishy design provides stress relief during challenging moments. Perfect for building confidence through gentle, approachable bravery.',
    specs: { 
      weight: '2.0 lbs', 
      dimensions: '10" x 10" x 8"', 
      color: 'Golden', 
      texture: 'Brave-Squishy Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Adorable round lion design with fluffy mane',
      'Golden plush that radiates warmth and courage',
      'Ultra-squishy texture perfect for stress relief',
      'Warm golden chest light symbolizing inner strength',
      'AI conversations focused on gentle courage building',
      'Approachable bravery in a huggable, comforting form'
    ],
    idealFor: [
      'Building courage in a gentle, approachable way',
      'Children who prefer softer expressions of bravery',
      'Combining confidence-building with comfort needs',
      'Stress relief during challenging or scary situations',
      'Tactile comfort while facing new experiences',
      'Children who need both courage and squeezable comfort'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: 'white', marginBottom: '10px' }}
          >
            Back to Lumi Bears
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🦁</span>
            <div>
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  borderRadius: '50%'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="orange">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#fff8dc' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications & Features */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            {Object.entries(product.specs).map(([key, value]) => (
              <Col xs={12} md={6} key={key}>
                <Text strong>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:</Text><br/>
                <Text>{value}</Text>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose the Lumi Squishy Lion?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#ffa500', marginBottom: '16px' }} />
              <Title level={4}>Gentle Courage</Title>
              <Text>
                Builds confidence and bravery in the most approachable way possible - through squeezable, comforting design.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#ff8c00', marginBottom: '16px' }} />
              <Title level={4}>Stress-Relief Bravery</Title>
              <Text>
                Perfect for children who need courage but also need tactile comfort to manage stress and anxiety.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#ffd700', marginBottom: '16px' }} />
              <Title level={4}>Huggable Strength</Title>
              <Text>
                Combines the confidence-inspiring nature of a lion with the ultimate comfort of a squishy, round design.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiSquishyLion;